/**
 * AuthGuard — listens for `auth:unauthorized` from the API client and routes
 * the user to `/login?next=<current>`.
 *
 * Wraps the SPA tree. Renders children verbatim; the only side-effect is the
 * window-level event listener. The SPA owns its own bearer (minted at login,
 * stored in sessionStorage), so there is no session→bearer exchange to run on
 * first paint — a 401 simply means the user is unauthenticated or the token
 * expired, and we send them to login.
 *
 * The login + health routes are exempt: on `/login` a 401 from
 * `/v1/auth/login` is already surfaced in the form, and `/health`'s whole
 * job is to display the API state including 401.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { AUTH_UNAUTHORIZED_EVENT, clearApiToken } from '@/lib/auth';

interface AuthGuardProps {
  children: ReactNode;
}

const NO_REDIRECT_PATHS = new Set<string>(['/login', '/health']);

export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Latest location, readable from the async event callback without re-binding.
  const locationRef = useRef(location);
  locationRef.current = location;

  const redirectToLogin = useCallback(() => {
    const loc = locationRef.current;
    if (NO_REDIRECT_PATHS.has(loc.pathname)) return;
    clearApiToken();
    navigate({
      to: '/login',
      search: { next: loc.pathname + loc.searchStr },
    });
  }, [navigate]);

  useEffect(() => {
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, redirectToLogin);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, redirectToLogin);
  }, [redirectToLogin]);

  return <>{children}</>;
}
